const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const SHORT_WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_INDEX_BY_NAME = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

const getDateObject = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const input = String(value).trim();
  if (!input) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
    const date = new Date(`${input}T12:00:00`);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (/^\d{2}:\d{2}(:\d{2})?$/.test(input)) {
    const normalized = input.length === 5 ? `${input}:00` : input;
    const date = new Date(`1970-01-01T${normalized}`);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(input);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatPlainNumber = (value) => {
  const formatter = new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return formatter.format(value);
};

const formatScaledNumber = (value, suffix) => {
  const formatter = new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${formatter.format(value)}${suffix}`;
};

// Read the configured date format (cached in localStorage by App.js on settings load).
const getConfiguredDateFormat = () => {
  try {
    return localStorage.getItem('pos_date_format') || 'DD/MM/YYYY';
  } catch {
    return 'DD/MM/YYYY';
  }
};

export const formatUiDate = (value, formatOverride) => {
  const date = getDateObject(value);
  if (!date) return value || '';
  const weekday = SHORT_WEEKDAYS[date.getDay()];
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const mon = SHORT_MONTHS[date.getMonth()];
  const yyyy = date.getFullYear();
  const fmt = formatOverride || getConfiguredDateFormat();
  switch (fmt) {
    case 'MM/DD/YYYY':
      return `${mm}/${dd}/${yyyy}`;
    case 'YYYY/MM/DD':
      return `${yyyy}/${mm}/${dd}`;
    case 'DAY, DD-MMM-YYYY':
      return `${weekday}, ${String(date.getDate())}-${mon}-${yyyy}`;
    case 'DD/MM/YYYY':
    default:
      return `${dd}/${mm}/${yyyy}`;
  }
};

export const formatUiMonthYear = (value) => {
  const date = getDateObject(value);
  if (!date) return value || '';
  return `${SHORT_MONTHS[date.getMonth()]} ${date.getFullYear()}`;
};

export const formatUiTime = (value, { seconds = false } = {}) => {
  const date = getDateObject(value);
  if (!date) return value || '';
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const secs = String(date.getSeconds()).padStart(2, '0');
  return seconds ? `${hours}:${minutes}:${secs}` : `${hours}:${minutes}`;
};

export const formatUiDateTime = (value, { seconds = false, separator = ' | ' } = {}) => {
  const date = getDateObject(value);
  if (!date) return value || '';
  return `${formatUiDate(date)}${separator}${formatUiTime(date, { seconds })}`;
};

export const formatUiHourLabel = (value) => {
  if (value == null || value === '') return '';

  if (typeof value === 'number' && !Number.isNaN(value)) {
    const hour = ((value % 24) + 24) % 24;
    return formatUiTime(`${String(hour).padStart(2, '0')}:00`);
  }

  const input = String(value).trim();
  if (!input) return '';

  if (/^\d{1,2}$/.test(input)) {
    const hour = Number(input);
    if (hour >= 0 && hour <= 23) {
      return formatUiTime(`${String(hour).padStart(2, '0')}:00`);
    }
  }

  let match = input.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (match) {
    let hour = Number(match[1]);
    const minute = Number(match[2] || '0');
    const meridiem = match[3].toUpperCase();
    if (meridiem === 'PM' && hour !== 12) hour += 12;
    if (meridiem === 'AM' && hour === 12) hour = 0;
    return formatUiTime(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
  }

  if (/^\d{2}:\d{2}(:\d{2})?$/.test(input)) {
    return formatUiTime(input, { seconds: input.length > 5 });
  }

  return input;
};

export const formatUiCurrencyCompact = (value, symbol = 'Rs.') => {
  const numeric = parseFloat(value || 0);
  if (Number.isNaN(numeric)) return `${symbol} 0`;

  const sign = numeric < 0 ? '-' : '';
  const absolute = Math.abs(numeric);

  let formatted;
  if (absolute >= 1_000_000_000) {
    formatted = formatScaledNumber(absolute / 1_000_000_000, 'B');
  } else if (absolute >= 1_000_000) {
    formatted = formatScaledNumber(absolute / 1_000_000, 'M');
  } else if (absolute >= 1_000) {
    formatted = formatScaledNumber(absolute / 1_000, 'K');
  } else {
    formatted = formatPlainNumber(absolute);
  }

  return `${sign}${symbol} ${formatted}`;
};

export const formatUiCurrencyDetailed = (value, symbol = 'Rs.') => {
  const numeric = parseFloat(value || 0);
  if (Number.isNaN(numeric)) return `${symbol} 0`;

  const sign = numeric < 0 ? '-' : '';
  const absolute = Math.abs(numeric);
  const formatter = new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  return `${sign}${symbol} ${formatter.format(absolute)}`;
};

export const formatUiRange = (fromDate, toDate) => {
  if (!fromDate || !toDate) return '';
  return `${formatUiDate(fromDate)} to ${formatUiDate(toDate)}`;
};

export const formatUiDateTimeRange = (fromDate, fromTime, toDate, toTime) => {
  if (!fromDate || !toDate) return '';
  const startTime = formatUiTime(fromTime || '00:00');
  const endTime = formatUiTime(toTime || '23:59');
  return `${startTime} ${formatUiDate(fromDate)} to ${endTime} ${formatUiDate(toDate)}`;
};

export const parseUiDateInput = (value) => {
  const input = String(value || '').trim();
  if (!input) return '';

  if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
    return input;
  }

  const strippedWeekday = input.replace(/^[A-Za-z]{3},\s*/, '');

  // YYYY/MM/DD order
  let match = strippedWeekday.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
  if (match) {
    const [, year, month, day] = match;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day));
    if (!Number.isNaN(parsed.getTime())) {
      return `${year}-${String(Number(month)).padStart(2, '0')}-${String(Number(day)).padStart(2, '0')}`;
    }
  }

  // DD/MM or MM/DD depending on configured format
  match = strippedWeekday.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (match) {
    const fmt = getConfiguredDateFormat();
    let day, month;
    if (fmt === 'MM/DD/YYYY') {
      [, month, day] = match;
    } else {
      [, day, month] = match;
    }
    const year = match[3];
    const parsed = new Date(Number(year), Number(month) - 1, Number(day));
    if (!Number.isNaN(parsed.getTime())) {
      return `${year}-${String(Number(month)).padStart(2, '0')}-${String(Number(day)).padStart(2, '0')}`;
    }
  }

  match = strippedWeekday.match(/^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/);
  if (match) {
    const [, day, monthName, year] = match;
    const monthIndex = MONTH_INDEX_BY_NAME[monthName.toLowerCase()];
    if (monthIndex != null) {
      const parsed = new Date(Number(year), monthIndex, Number(day));
      if (!Number.isNaN(parsed.getTime())) {
        return `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(Number(day)).padStart(2, '0')}`;
      }
    }
  }

  const parsed = new Date(input);
  if (!Number.isNaN(parsed.getTime())) {
    return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
  }

  return '';
};

export const normalizeUiTimeInput = (value) => {
  const input = String(value || '').trim();
  if (!input) return '';

  // Accept "HH:MM:SS" (e.g. a backend TimeField "11:00:00") by dropping the seconds.
  const withSeconds = input.match(/^(\d{1,2}):(\d{2}):\d{2}$/);
  if (withSeconds) {
    const hour = Number(withSeconds[1]);
    const minute = Number(withSeconds[2]);
    if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
      return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    }
    return '';
  }

  if (/^\d{2}:\d{2}$/.test(input)) {
    const [hour, minute] = input.split(':').map(Number);
    if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
      return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    }
    return '';
  }

  if (/^\d{1,2}$/.test(input)) {
    const hour = Number(input);
    if (hour >= 0 && hour <= 23) {
      return `${String(hour).padStart(2, '0')}:00`;
    }
    return '';
  }

  const digits = input.replace(/\D/g, '');
  if (digits.length === 3 || digits.length === 4) {
    const normalized = digits.length === 3 ? `0${digits}` : digits;
    const hour = Number(normalized.slice(0, 2));
    const minute = Number(normalized.slice(2, 4));
    if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
      return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    }
  }

  return '';
};
